import type { PatientUser } from '~/shared/types'
import { formatApiDate } from '~/shared/lib/date'
import { Box, IconButton, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { TaskV2ListResponse } from '@fireflyhealth/api'
import SingleTaskEditor from './SingleTaskEditor'
import { CheckBox, PriorityIcon } from '~/shared/ui'
import TaskDueDate from './TaskDueDate'
import { fireAndForget } from '~/shared/lib/fireAndForget'

export type InlineTaskItem = TaskV2ListResponse['results'][number]

interface InlineTaskProps {
  task: InlineTaskItem
  onChecked: (isComplete: boolean) => void | Promise<void>
}

const SUB_TEXT_SX = { fontStyle: 'italic', color: 'grey.600', fontSize: '1.25rem' }

const InlineTask = (props: InlineTaskProps) => {
  const { task } = props
  const users = useSelector(state => state.users.byId)
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const createdBy = task.created_by_user
  const createdByName = [createdBy?.first_name, createdBy?.last_name].filter(Boolean).join(' ')
  // `users.byId` holds patients and providers alike; the inline editor needs a patient with a
  // Person, which is what the editor chain reads.
  const storedUser = users[task.patient ?? '']
  const patient =
    storedUser && 'person' in storedUser && storedUser.person ? (storedUser as PatientUser) : null

  let assignee =
    [...(patient ? [patient] : []), ...providers].find(
      user => user.assignee_group?.id === task.owner_group,
    )?.assignee_group ?? null

  assignee ??= assigneeGroups.find(group => group.id === task.owner_group) ?? null

  let completedAtHtml: ReactNode = ''
  if (task.completed_by_user) {
    const completedBy = task.completed_by_user
    const completedByName = [completedBy.first_name, completedBy.last_name]
      .filter(Boolean)
      .join(' ')
    completedAtHtml = (
      <Typography sx={SUB_TEXT_SX}>
        — Completed{completedByName ? ` by ${completedByName}` : ''} at
        {` ${formatApiDate(task.completed_on, 'EEE, MMM d, yyyy h:mm a') ?? ''}`}
      </Typography>
    )
  }

  const onChecked = async (value: boolean) => {
    setLoading(true)
    try {
      await props.onChecked(value)
    } finally {
      setLoading(false)
    }
  }

  return editing && patient ? (
    <SingleTaskEditor patient={patient} task={task} handleClose={() => setEditing(false)} />
  ) : (
    <Box
      sx={[
        {
          position: 'relative',
          bgcolor: 'common.white',
          margin: '1rem 0 0 0rem',
          padding: '1rem 0 1rem 6rem',
          border: theme => `solid ${theme.palette.secondary.lighter} .1rem`,
          borderRadius: '.5rem',
          '& button': { position: 'absolute', top: '1.4rem', left: '2rem' },
        },
        loading && { opacity: 0.5, pointerEvents: 'none' },
      ]}
    >
      <CheckBox
        value={task.is_complete}
        onChecked={value => fireAndForget('[Task] toggle complete', () => onChecked(value))}
      />
      <Typography
        sx={{ paddingRight: '4rem', textDecoration: task.is_complete ? 'line-through' : undefined }}
      >
        {task.priority ? <PriorityIcon level={task.priority} variant="view" /> : null}
        {task.title}
      </Typography>
      <Typography sx={{ fontSize: '1.25rem', marginBottom: '.3rem' }}>
        <Box component="span" sx={{ fontWeight: 800, color: 'common.black', paddingLeft: '.3rem' }}>
          Assigned to{' '}
        </Box>{' '}
        {assignee?.name}{' '}
        <TaskDueDate dueDate={task.due_date} isComplete={Boolean(task.is_complete)} />
      </Typography>
      <Typography sx={SUB_TEXT_SX}>
        — Created{createdByName ? ` by ${createdByName}` : ''} on
        {` ${formatApiDate(task.created_at, 'EEE, MMM d, yyyy h:mm a') ?? ''}`}
      </Typography>
      {completedAtHtml}
      {!task.is_complete ? (
        <IconButton
          sx={{ position: 'absolute', top: '0 !important', right: '0', left: 'initial !important' }}
          onClick={() => setEditing(true)}
          value={task.id}
        >
          <EditIcon sx={{ color: 'secondary.light' }} />
        </IconButton>
      ) : null}
    </Box>
  )
}

export default InlineTask
